import { el } from "./dom";
import { createCrestButton } from "./CrestButton";
import { installFocusTrap, type FocusTrapHandle } from "../focusTrap";

export interface ModalAction {
  readonly label: string;
  readonly primary?: boolean;
  readonly onClick: () => void;
}

export interface ModalOptions {
  readonly title: string;
  readonly body: string | HTMLElement;
  readonly actions: readonly ModalAction[];
  readonly dismissible?: boolean;
}

export interface ModalHandle {
  readonly close: () => void;
  readonly setTitle: (title: string) => void;
  readonly setBody: (body: string | HTMLElement) => void;
  readonly setActions: (actions: readonly ModalAction[]) => void;
  /**
   * Focus the primary action button inside *this* modal (falls back to the
   * first button when no action is marked primary). Scoped to the modal's
   * own card so a stacked sub-dialog opened on top can still focus its own
   * buttons without picking up the outer modal's. Returns true when a button
   * was found and focused.
   */
  readonly focusPrimary: () => boolean;
}

let titleIdSeq = 0;

export function openModal(opts: ModalOptions): ModalHandle {
  const dismissible = opts.dismissible !== false;
  const titleId = `bn-modal-title-${++titleIdSeq}`;
  const overlay = el("div", { class: "modal-overlay" });
  const card = el("div", {
    class: "modal-card",
    attrs: {
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": titleId,
      tabindex: "-1",
    },
  });
  const titleNode = el("h2", {
    class: "modal-title",
    text: opts.title,
    attrs: { id: titleId },
  });
  const bodyNode = el("div", { class: "modal-body" });
  const actionsNode = el("div", { class: "modal-actions" });

  function fillBody(body: string | HTMLElement): void {
    while (bodyNode.firstChild) bodyNode.removeChild(bodyNode.firstChild);
    if (typeof body === "string") {
      bodyNode.appendChild(document.createTextNode(body));
    } else {
      bodyNode.appendChild(body);
    }
  }

  function fillActions(actions: readonly ModalAction[]): void {
    while (actionsNode.firstChild) actionsNode.removeChild(actionsNode.firstChild);
    for (const a of actions) {
      const btn = createCrestButton({
        label: a.label,
        primary: a.primary,
        small: true,
        onClick: () => a.onClick(),
      });
      actionsNode.appendChild(btn);
    }
  }

  fillBody(opts.body);
  fillActions(opts.actions);

  card.appendChild(titleNode);
  card.appendChild(bodyNode);
  card.appendChild(actionsNode);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const trap: FocusTrapHandle = installFocusTrap(card);
  let closed = false;

  function close(): void {
    if (closed) return;
    closed = true;
    trap.release();
    overlay.remove();
  }

  if (dismissible) {
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });
  }

  return {
    close,
    setTitle: (title) => {
      titleNode.textContent = title;
    },
    setBody: fillBody,
    setActions: fillActions,
    focusPrimary: () => {
      // Query scoped to *this* modal's actions row so stacked modals each
      // focus their own primary instead of fighting over `document.querySelector`.
      const primary = actionsNode.querySelector<HTMLButtonElement>(
        "button.crest-btn--primary",
      ) ?? actionsNode.querySelector<HTMLButtonElement>("button");
      if (primary === null) return false;
      primary.focus();
      return true;
    },
  };
}
